/**
 * Report Studio engine: per-column filtering, sorting, grouping, and totals.
 *
 * Lives apart from helpers.ts because it needs the column definitions
 * (helpers.ts must stay import-free of columns.tsx to avoid a cycle).
 */
import type { Loan } from '@/types'
import { LOAN_STAGES } from '@/types'
import type { ColumnDef } from './columnTypes'
import { COLUMN_MAP } from './columns'
import { money, pct } from './helpers'
import type { AggMethod, ColumnFilterValue, ColumnId, ReportConfig } from './types'

const MISSING = '—'

function displayOf(column: ColumnDef, loan: Loan): string {
  return column.display(loan)
}

function matchesColumnFilter(column: ColumnDef, filter: ColumnFilterValue, loan: Loan): boolean {
  if (column.kind === 'enum') {
    const values = filter.values ?? []
    if (values.length === 0) return true
    return values.includes(displayOf(column, loan))
  }
  if (column.kind === 'text') {
    const needle = (filter.contains ?? '').trim().toLowerCase()
    if (!needle) return true
    return displayOf(column, loan).toLowerCase().includes(needle)
  }
  if (column.kind === 'number') {
    const hasMin = filter.min != null && !Number.isNaN(filter.min)
    const hasMax = filter.max != null && !Number.isNaN(filter.max)
    if (!hasMin && !hasMax) return true
    const value = column.num?.(loan) ?? null
    if (value == null) return false
    if (hasMin && value < (filter.min as number)) return false
    if (hasMax && value > (filter.max as number)) return false
    return true
  }
  if (column.kind === 'date') {
    const from = filter.from ?? ''
    const to = filter.to ?? ''
    if (!from && !to) return true
    const raw = String(column.value(loan) || '')
    if (!raw) return false
    if (from && raw.slice(0, 10) < from) return false
    if (to && raw.slice(0, 10) > to) return false
    return true
  }
  return true
}

/** Apply the report designer's per-column filters and date range. */
export function applyReportFilters(loans: Loan[], report?: ReportConfig | null): Loan[] {
  if (!report) return loans
  const active = Object.entries(report.columnFilters ?? {}).filter(([, filter]) => filter != null)
  const dateFrom = report.dateFrom || ''
  const dateTo = report.dateTo || ''
  if (active.length === 0 && !dateFrom && !dateTo) return loans

  return loans.filter((loan) => {
    if (dateFrom || dateTo) {
      const originated = (loan.originated || '').slice(0, 10)
      if (!originated) return false
      if (dateFrom && originated < dateFrom) return false
      if (dateTo && originated > dateTo) return false
    }
    for (const [columnId, filter] of active) {
      const column = COLUMN_MAP.get(columnId as ColumnId)
      if (!column || !filter) continue
      if (!matchesColumnFilter(column, filter, loan)) return false
    }
    return true
  })
}

/** True when the filter would actually constrain rows (used for chips/badges). */
export function isActiveColumnFilter(filter: ColumnFilterValue | undefined): boolean {
  if (!filter) return false
  if ((filter.values?.length ?? 0) > 0) return true
  if ((filter.contains ?? '').trim()) return true
  if (filter.min != null || filter.max != null) return true
  if (filter.from || filter.to) return true
  return false
}

export function sortLoans(loans: Loan[], sortId: ColumnId, direction: 'asc' | 'desc'): Loan[] {
  const column = COLUMN_MAP.get(sortId)
  const rows = [...loans]
  rows.sort((a, b) => {
    const left = column?.value(a) ?? ''
    const right = column?.value(b) ?? ''
    const order =
      typeof left === 'number' && typeof right === 'number'
        ? direction === 'asc' ? left - right : right - left
        : direction === 'asc'
          ? String(left).localeCompare(String(right))
          : String(right).localeCompare(String(left))
    // Ties by deal id, as the grid and the schedules break them.
    return order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  })
  return rows
}

export function groupLoans(
  loans: Loan[],
  groupBy: ColumnId | '',
): Array<{ key: string; rows: Loan[] }> {
  if (!groupBy) return [{ key: '', rows: loans }]
  const column = COLUMN_MAP.get(groupBy)
  if (!column) return [{ key: '', rows: loans }]
  const buckets = new Map<string, Loan[]>()
  for (const loan of loans) {
    const key = displayOf(column, loan)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(loan)
    else buckets.set(key, [loan])
  }
  const entries = [...buckets.entries()]
  // Pipeline stage groups follow the desk's canonical order, not A–Z.
  if (groupBy === 'stage') {
    entries.sort((a, b) => {
      const ia = LOAN_STAGES.indexOf(a[0] as (typeof LOAN_STAGES)[number])
      const ib = LOAN_STAGES.indexOf(b[0] as (typeof LOAN_STAGES)[number])
      const left = ia < 0 ? Number.MAX_SAFE_INTEGER : ia
      const right = ib < 0 ? Number.MAX_SAFE_INTEGER : ib
      if (left !== right) return left - right
      return a[0].localeCompare(b[0])
    })
  } else {
    entries.sort((a, b) => a[0].localeCompare(b[0]))
  }
  return entries.map(([key, rows]) => ({ key, rows }))
}

/** Aggregate a column over rows. Returns null when no method or no usable values. */
export function aggregate(loans: Loan[], columnId: ColumnId, method: AggMethod): number | null {
  const column = COLUMN_MAP.get(columnId)
  if (!column || method === 'none') return null
  if (method === 'count') {
    return loans.filter((loan) => displayOf(column, loan) !== MISSING).length
  }
  if (method === 'distinct') {
    return new Set(
      loans.map((loan) => displayOf(column, loan)).filter((value) => value !== MISSING),
    ).size
  }
  const values = loans
    .map((loan) => column.num?.(loan) ?? null)
    .filter((value): value is number => value != null && !Number.isNaN(value))
  if (values.length === 0) return null
  switch (method) {
    case 'sum':
      return values.reduce((sum, value) => sum + value, 0)
    case 'avg':
      return values.reduce((sum, value) => sum + value, 0) / values.length
    case 'min':
      return Math.min(...values)
    case 'max':
      return Math.max(...values)
    default:
      return null
  }
}

export function formatAggValue(value: number | null, columnId: ColumnId, method: AggMethod): string {
  if (value == null) return MISSING
  if (method === 'count' || method === 'distinct') return String(value)
  const column = COLUMN_MAP.get(columnId)
  switch (column?.unit) {
    case 'money':
      return money(value)
    case 'pct':
      return pct(value)
    case 'points':
      // The governance completeness score is deliberately a whole percent
      // (83 means 83%). Formatting it through pct() read 8300%.
      return `${value.toFixed(1)}%`
    case 'x':
      return `${value.toFixed(2)}x`
    case 'days':
      return `${Math.round(value)}d`
    case 'months':
      return String(Math.round(value))
    case 'int':
      return String(Math.round(value))
    default:
      return value.toLocaleString()
  }
}

export const AGG_LABELS: Record<AggMethod, string> = {
  none: 'None',
  sum: 'Total',
  avg: 'Average',
  count: 'Count',
  distinct: 'Distinct',
  min: 'Min',
  max: 'Max',
}

/** Methods that make sense for a column of the given kind. */
export function aggMethodsFor(column: ColumnDef): AggMethod[] {
  if (column.kind === 'number') return ['none', 'sum', 'avg', 'min', 'max', 'count']
  return ['none', 'count', 'distinct']
}

export function totalsLabelFor(config: ReportConfig, columnId: ColumnId): string {
  const method = config.totals[columnId] ?? 'none'
  return method === 'none' ? '' : AGG_LABELS[method]
}
